import React, { useRef, useState } from "react";
import axios from "axios";
import contactDefault from "../../data/contact.json";

const backendUrl = process.env.REACT_APP_DATABASEURL;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRe = /^[6-9]\d{9}$/;

const QueryForm = ({ contact = contactDefault }) => {
  const { queryForm, subjects } = contact;
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", subject: "", description: "", consent: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const refs = useRef({});

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 3) e.name = "Please enter your full name.";
    if (!mobileRe.test(form.mobile)) e.mobile = "Enter a valid 10-digit mobile number.";
    if (!emailRe.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.subject) e.subject = "Please select a subject.";
    if (form.description.trim().length < 10) e.description = "Tell us a little more (min 10 characters).";
    if (!form.consent) e.consent = "Please accept to continue.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setStatus(null);
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      refs.current[Object.keys(e)[0]]?.focus();
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(
        `${backendUrl}api/add_Enquiry_user`,
        {
          name: form.name.trim(),
          mobile_number: form.mobile.trim(),
          email: form.email.trim(),
          subject: form.subject,
          message: form.description.trim(),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200 || res.status === 201) {
        setStatus({ type: "ok", text: "Thanks for reaching out! We've received your query and will respond soon." });
        setForm({ name: "", mobile: "", email: "", subject: "", description: "", consent: false });
      } else {
        setStatus({ type: "err", text: "Something went wrong. Please try again." });
      }
    } catch (err) {
      setStatus({ type: "err", text: "We couldn't submit your query right now. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  const reg = (key) => (el) => { refs.current[key] = el; };
  const cls = (key, base) => `${base}${errors[key] ? " is-invalid" : ""}`;

  return (
    <div className="con-card">
      <div className="con-card__head">
        <h2 className="con-card__title">
          Submit Your <span className="con-query__title-mark">Query</span>
        </h2>
        <p className="con-card__sub">Send us a message and we'll get back to you as soon as possible.</p>
      </div>
      <div className="con-card__body">
        <form onSubmit={handleSubmit} noValidate>
          <div className="con-form__grid">
            <div className="con-field">
              <label className="con-field__label" htmlFor="qf-name">
                Full Name<span className="con-field__req">*</span>
              </label>
              <input
                id="qf-name" ref={reg("name")} type="text" autoComplete="name"
                className={cls("name", "con-input")} placeholder="John Doe"
                value={form.name} onChange={(e) => set("name", e.target.value)}
                aria-invalid={!!errors.name}
              />
              {errors.name && <span className="con-field__error" role="alert">{errors.name}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="qf-mobile">
                Mobile Number<span className="con-field__req">*</span>
              </label>
              <input
                id="qf-mobile" ref={reg("mobile")} type="tel" inputMode="numeric" autoComplete="tel"
                maxLength={10} className={cls("mobile", "con-input")} placeholder="9876543210"
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                aria-invalid={!!errors.mobile}
              />
              {errors.mobile && <span className="con-field__error" role="alert">{errors.mobile}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="qf-email">
                Email ID<span className="con-field__req">*</span>
              </label>
              <input
                id="qf-email" ref={reg("email")} type="email" autoComplete="email"
                className={cls("email", "con-input")} placeholder="john@example.com"
                value={form.email} onChange={(e) => set("email", e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && <span className="con-field__error" role="alert">{errors.email}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="qf-subject">
                Subject<span className="con-field__req">*</span>
              </label>
              <div className="con-select-wrap">
                <select
                  id="qf-subject" ref={reg("subject")} className={cls("subject", "con-select")}
                  value={form.subject} onChange={(e) => set("subject", e.target.value)}
                  aria-invalid={!!errors.subject}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {errors.subject && <span className="con-field__error" role="alert">{errors.subject}</span>}
            </div>

            <div className="con-field con-field--full">
              <label className="con-field__label" htmlFor="qf-desc">
                Description<span className="con-field__req">*</span>
              </label>
              <textarea
                id="qf-desc" ref={reg("description")} className={cls("description", "con-textarea")}
                placeholder="Tell us more about your query…"
                value={form.description} onChange={(e) => set("description", e.target.value)}
                aria-invalid={!!errors.description}
              />
              {errors.description && <span className="con-field__error" role="alert">{errors.description}</span>}
            </div>
          </div>

          <div className="con-consent">
            <input
              id="qf-consent" ref={reg("consent")} type="checkbox"
              checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
            />
            <label htmlFor="qf-consent">
              {queryForm.consent}<span className="con-field__req">*</span>
            </label>
          </div>
          {errors.consent && <span className="con-field__error" role="alert">{errors.consent}</span>}

          <div className="con-submit-row">
            <button type="submit" className="btn-ed btn-ed--primary con-submit" disabled={loading}>
              {loading && <span className="con-spinner" aria-hidden="true" />}
              {loading ? "Sending…" : "Submit Query"}
            </button>
          </div>

          {status && (
            <div className={`con-status con-status--${status.type === "ok" ? "ok" : "err"}`} role="status" aria-live="polite">
              {status.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default QueryForm;
